import {
    Component,
    ElementRef,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { NgbDateAdapter, NgbDateStruct } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import subDays from 'date-fns/subDays';
import { interval } from 'rxjs';
import { filter, pairwise, startWith, tap } from 'rxjs/operators';

import {
    InteractionEntity,
    InteractionScoringEntity,
    InteractionScoringEntityType,
    NoteEntity,
    NoteEntityType,
} from '../../coaching-base/config';
import { FocusDto, NoteCategory } from '../../coaching-base/general';
import { NotesComponent } from '../../coaching-note/notes.component';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { disuseStore, useStore } from '../../store.decorator';
import { TranslationKeyMap } from '../coaching-observation.i18n';
import { CmpsForm, CmpsFormBuilder } from '../experimental.form';
import { ObservationFormDecorateConnector } from '../form-decorator/observation-form-decorate-connector';
import { ObservationFormDecoratorService } from '../form-decorator/observation-form-decorator.service';
import {
    ObservationMenuItem,
    ObservationMenuItemType,
} from '../observation-menu-item';
import {
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from '../observation/observation.model';
import { RootState } from '../observation/observation.state';
import { InteractionValidationResult } from '../observation/validation/interaction-validation-result.model';
import { NoteValidationResult } from '../observation/validation/note-validation-result.model';
import { ScoreValidationResult } from '../observation/validation/score-validation-result.model';

import { InteractionFormValue } from './interaction-form-value.model';
import {
    defaultQueueId,
    interactionDateTimeTransformer,
    InteractionFormFields,
    interactionFormValueTransformer,
} from './interaction.observation.form';
import { adjustDropdownWidth } from './interaction.observation.function';

@Component({
    selector: '[cmpsObservationInteractionNext]',
    templateUrl: './interaction.observation.component.html',
    host: {
        'data-e2e': 'component-interaction',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InteractionObservationComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InteractionObservationComponent),
            multi: true,
        },
        DateProviders,
    ],
})
@TakeUntilDestroy()
export class InteractionObservationComponent
    implements
        OnInit,
        OnDestroy,
        ControlValueAccessor,
        Validator,
        TakeUntilDestroy,
        ObservationFormDecorateConnector
{
    state: RootState; // TODO remove. for this type of redux pattern... refactor to have this component not need 'ROOT'
    @Input() storePathPrefix: string;

    @ViewChild(NotesComponent) readonly notes: NotesComponent;
    @ViewChild('dropDownQueue', { read: ElementRef }) _dropdownRef: ElementRef;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _InteractionFormFields = InteractionFormFields;
    @Input() config: InteractionEntity;
    @Input() published: boolean;
    @Input() focus_: FocusDto; // null/undefined = show all
    @Input() defaultNoteCategory: NoteCategory;
    /** @private */ _noteEntity: NoteEntity;
    /** @private */ _scoringEntity: InteractionScoringEntity;
    /** @private */ _interactionTypeOpts: Array<{
        value: number;
        label: string;
    }>;

    /** @private */ _queueOpts: Array<{ value: number; label: string }>;
    /** @private */ _interactionTriggerOpts: IOption[];
    /** @private */ _maxDate: Date;
    /** @private */ _minDateStruct: NgbDateStruct;
    /** @private */ _maxDateStruct: NgbDateStruct;
    /** @private */ _form: CmpsForm<InteractionFormFields>;
    private _blackout: boolean;
    private _initialValue: InteractionFormValue;
    private _item: InteractionFormValue;
    private _onChange: (_: InteractionFormValue) => void;
    private _onTouched: () => void;
    private _onValidatorChange: () => void;
    private _onRemove: () => void;

    /** @private */ _menuOpts: ObservationMenuItem[];

    /** @private */ get _value(): InteractionFormValue {
        return this._form.form.value;
    }

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngbDateAdapter: NgbDateAdapter<Date>,
        private readonly _translateService: TranslateService,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _dialog: CmpsDialog,
        private readonly _formDecoratorService: ObservationFormDecoratorService,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
        private readonly _renderer: Renderer2,
    ) {}

    ngOnInit(): void {
        /* TODO FIXME refactor out */
        // same as parent
        useStore(this, `${this.storePathPrefix}.root`);

        this._form = this._formBuilder.create<InteractionFormFields>(
            InteractionFormFields,
        );

        this._loadContextMenu();

        this._form.form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                startWith(null as InteractionFormValue),
                pairwise(),
                tap<[InteractionFormValue, InteractionFormValue]>(
                    ([prev, value]) => {
                        /* 'cause of the side effects of things that touch the form, change tracking is done like this */
                        prev = prev || this._initialValue;
                        const next: InteractionFormValue = {
                            ...value,
                            trackingTime: Date.now(),
                            trackingChanged:
                                prev.trackingChanged ||
                                value.trackingChanged ||
                                prev.interactionType !==
                                    value.interactionType ||
                                prev.interactionPurposeId !==
                                    value.interactionPurposeId ||
                                prev.identifier !== value.identifier ||
                                (!prev.time ? null : prev.time.getTime()) !==
                                    (!value.time
                                        ? null
                                        : value.time.getTime()) ||
                                (!prev.date ? null : prev.date.getTime()) !==
                                    (!value.date
                                        ? null
                                        : value.date.getTime()) ||
                                prev.queue !== value.queue ||
                                value.notes.some(
                                    (e) => e.changed,
                                ) /* TODO revise should add/remove note cause trackingChanged */ ||
                                value.scoring.some((e) => e.trackingChanged),
                        };

                        // TODO revise. better change check operability
                        this._form.form.patchValue(
                            {
                                trackingTime: next.trackingTime,
                                trackingChanged: next.trackingChanged,
                            },
                            { emitEvent: false },
                        );

                        CMPS_APP_DEF.ENV !== 'prod' &&
                            console.log('_onChange interaction', next);
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onChange && this._onChange(next);
                    },
                ),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._form.form.controls[InteractionFormFields.DATE.name].valueChanges
            .pipe(
                tap<Date>((value) => {
                    const date = interactionDateTimeTransformer(
                        value,
                        this._form.value[InteractionFormFields.TIME.name],
                    );
                    this._form.form.controls[
                        InteractionFormFields.TIME.name
                    ].patchValue(date);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._noteEntity = this.config.childEntities.find(
            (e) => e.entityType === NoteEntityType,
        ) as NoteEntity;
        this._scoringEntity = this.config.childEntities.find(
            (e) => e.entityType === InteractionScoringEntityType,
        ) as InteractionScoringEntity;

        this._interactionTypeOpts = this.config.interactionTypes.map((e) => ({
            value: e.interactionTypeId,
            label: e.interactionTypeName,
        }));

        this._queueOpts = this.state.queues.map((e) => ({
            value: e.id,
            label: e.name,
        }));

        // will probably change
        this._queueOpts.unshift({
            value: defaultQueueId /* check api */,
            label: this._translateService.instant(TranslationKeyMap._842_N_A),
        });

        this._interactionTriggerOpts = [...this.config.interactionPurpose]
            .sort(
                (a, b) =>
                    (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
                    (b.displayOrder ?? Number.MAX_SAFE_INTEGER),
            )
            .map((e) => ({
                value: e.interactionPurposeId,
                label: e.interactionPurposeName,
            }));

        this._watchDateTime();
    }

    ngOnDestroy(): void {
        /* TODO FIXME refactor out */
        disuseStore(this);

        this._formDecoratorService.unRegister(this);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });

        this._loadContextMenu();
    }

    writeValue(obj: InteractionFormValue): void {
        this._item = obj || ({} as any as InteractionFormValue); // don't use createInteraction()
        this._patchForm();
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.form.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
        });
        this._form.checkWarningsErrors();
        return this._form.form.errors;
    }

    fdcDecorate(result: InteractionValidationResult): void {
        const note = result.notes.find((e) => !e.valid);
        note && this._decorateNote(note, note.valid);

        const score = result.scores.find((e) => !e.valid);
        score && this._decorateScore(score, score.valid);
    }

    fdcDecorateDelta(
        result: InteractionValidationResult,
        deltaResult: InteractionValidationResult,
    ): void {
        const note = result.notes.find((e) => !e.valid);
        if (note) {
            const noteDelta = deltaResult.notes.find(
                (e) => e.trackingId === note.trackingId,
            );
            noteDelta && this._decorateNote(noteDelta, noteDelta.valid);
        }

        const score = result.scores.find((e) => !e.valid);
        if (score) {
            const scoreDelta = deltaResult.scores.find(
                (e) => e.trackingId === score.trackingId,
            );
            scoreDelta && this._decorateScore(scoreDelta, scoreDelta.valid);
        }
    }

    fdcUnDecorate(result: InteractionValidationResult): void {
        const noteSelectors = result.notes
            .map((e) => `[data-trackingid='${e.trackingId}']`)
            .join(',');
        if (noteSelectors.length !== 0) {
            Array.from(
                this._el.nativeElement.querySelectorAll(noteSelectors),
            ).forEach((el) => el.classList.remove('outline_box'));
        }

        const scoreSelectors = result.scores
            .map((e) => `[data-trackingid='${e.trackingId}']`)
            .join(',');
        if (scoreSelectors.length !== 0) {
            Array.from(this._el.nativeElement.querySelectorAll(scoreSelectors))
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                .forEach(
                    (el) =>
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        el.parentElement &&
                        el.parentElement.classList.remove('outline_box'),
                );
        }
    }

    focus(): void {
        //
    }

    /** @private */ _onCancelClick(): void {
        if (this._value.trackingChanged) {
            // TODO revise form.pristine?
            this._discard();
        } else {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._onRemove && this._onRemove();
        }
    }

    /* @private */
    _onDropdownOpen(): void {
        adjustDropdownWidth(this._renderer, this._dropdownRef);
    }

    private _watchDateTime(): void {
        const check = (): void => {
            let minDate = new Date();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (this.config && this.config.dateRestriction) {
                minDate = subDays(
                    new Date(),
                    this.config.dateRestriction.maxPastDays,
                );
            }
            this._maxDate = new Date(Date.now() + 30000);
            this._minDateStruct = this._ngbDateAdapter.fromModel(minDate);
            this._maxDateStruct = this._ngbDateAdapter.fromModel(this._maxDate);
        };
        // using a property accessor would get called too often and then the control also invokes the fn passed in Validator.registerOnValidatorChange
        interval(60000)
            .pipe(
                tap(() => check()),
                takeUntilDestroy(this),
            )
            .subscribe();
        check();
    }

    private _patchForm(): void {
        this._blackout = true;

        this._initialValue = this._item;
        this._form.clearWarningsErrors();
        this._form.form.reset(this._initialValue, { emitEvent: false });

        this._loadContextMenu();

        this._formDecoratorService.unRegister(this);
        this._formDecoratorService.registerForTrackingId(
            this._value.trackingId,
            this,
        );

        this._blackout = false;
    }

    onRemove(fn: any): void {
        this._onRemove = fn;
    }

    private _discard(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9492_WARNING,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9699_DISCARD_CHANGES_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    if (this._value.interactionId != null) {
                        const orig = this._value.trackingOriginal;
                        const next = interactionFormValueTransformer(
                            this._value.trackingOriginal,
                        );
                        next.trackingId = this._value.trackingId;
                        next.trackingTime = this._value.trackingTime;
                        next.trackingOriginal = orig;

                        this._item = next;
                        this._patchForm();

                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onChange && this._onChange(next);
                    } else {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onRemove && this._onRemove();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onMenuItemClick(item: ObservationMenuItem): void {
        if (item.type === ObservationMenuItemType.DELETE) {
            this._onCancelClick();
        }
    }

    private _loadContextMenu(): void {
        this._menuOpts = [];

        if (!this._form.form.disabled && !this.published) {
            this._menuOpts.push({
                type: ObservationMenuItemType.DELETE,
                label: this._translateService.instant(
                    TranslationKeyMap._7424_DELETE,
                ),
                icon: 'delete',
            });
        }
    }

    private _decorateNote(note: NoteValidationResult, valid: boolean): void {
        const el = this._el.nativeElement.querySelector(
            `[data-trackingid='${note.trackingId}']`,
        );
        if (el) {
            if (!valid) {
                el.classList.add('outline_box');
            } else {
                el.classList.remove('outline_box');
            }
        }
    }

    private _decorateScore(score: ScoreValidationResult, valid: boolean): void {
        const el = this._el.nativeElement.querySelector(
            `[data-trackingid='${score.trackingId}']`,
        );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (el && el.parentElement) {
            if (!valid) {
                el.parentElement.classList.add('outline_box');
            } else {
                el.parentElement.classList.remove('outline_box');
            }
        }
    }
}
